/**
 * Hooks para gestión de roles y asignaciones
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { roleService } from "@/lib/services/roles";
import type { SystemRole, ScopeType } from "@/types";

// ==================== QUERY KEYS ====================

export const roleKeys = {
  all: ["roleAssignments"] as const,
  byUser: (userId: string) => [...roleKeys.all, "user", userId] as const,
  byScope: (scopeType: ScopeType, scopeId: string) =>
    [...roleKeys.all, "scope", scopeType, scopeId] as const,
};

// ==================== QUERY HOOKS ====================

/**
 * Obtiene todas las asignaciones de roles
 */
export function useRoleAssignments() {
  return useQuery({
    queryKey: roleKeys.all,
    queryFn: () => roleService.getAll(),
  });
}

/**
 * Obtiene asignaciones de un usuario específico
 */
export function useUserRoles(userId: string | undefined) {
  return useQuery({
    queryKey: roleKeys.byUser(userId!),
    queryFn: () => roleService.getByUserId(userId!),
    enabled: !!userId,
  });
}

/**
 * Obtiene asignaciones para un scope específico
 */
export function useScopeRoles(
  scopeType: ScopeType,
  scopeId: string | undefined,
) {
  return useQuery({
    queryKey: roleKeys.byScope(scopeType, scopeId!),
    queryFn: () => roleService.getByScope(scopeType, scopeId!),
    enabled: !!scopeId,
  });
}

// ==================== MUTATION HOOKS ====================

/**
 * Asigna un rol a un usuario
 */
export function useAssignRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      userId: string;
      role: SystemRole;
      scopeType: ScopeType;
      scopeId: string;
      scopeName: string;
      assignedBy: string;
    }) => {
      return roleService.assignRole(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      toast.success("Rol asignado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al asignar rol: ${error.message}`);
    },
  });
}

/**
 * Revoca un rol de un usuario
 */
export function useRevokeRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { assignmentId: string; revokedBy: string }) => {
      return roleService.revokeRole(params.assignmentId, params.revokedBy);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      toast.success("Rol revocado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al revocar rol: ${error.message}`);
    },
  });
}
